import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from 'components/ui/Header';
import Footer from '../landing-page/components/Footer';
import { supabase } from 'lib/supabase';

const UpdatePassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [sessionValid, setSessionValid] = useState(null);

  useEffect(() => {
    let timeout;

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        clearTimeout(timeout);
        setSessionValid(true);
      }
    });

    timeout = setTimeout(() => {
      setSessionValid((current) => {
        if (current === null) return false;
        return current;
      });
    }, 3000);

    return () => {
      subscription?.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (password?.length < 8) {
      setError('La password deve contenere almeno 8 caratteri.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Le password non coincidono.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase?.auth?.updateUser({ password });
      if (updateError) {
        if (updateError?.message?.includes('different from the old password')) {
          setError('La nuova password deve essere diversa da quella attuale.');
        } else {
          setError('Errore durante l\'aggiornamento. Riprova.');
        }
      } else {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login-form');
        }, 3000);
      }
    } catch (err) {
      setError('Si è verificato un errore imprevisto. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md mx-auto mt-8">
          <div className="bg-card border border-border rounded-2xl shadow-sm p-8">
            {sessionValid === null ? (
              <div className="text-center py-4">
                <p className="text-muted-foreground text-sm">Verifica in corso...</p>
              </div>
            ) : !sessionValid ? (
              <div className="text-center">
                <div className="flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mx-auto mb-4">
                  <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z" />
                  </svg>
                </div>
                <p className="text-foreground text-base leading-relaxed mb-6">
                  Link scaduto o non valido. Richiedi un nuovo link.
                </p>
                <Link
                  to="/login-form"
                  className="inline-block py-2.5 px-6 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors duration-200"
                >
                  Torna al login
                </Link>
              </div>
            ) : success ? (
              <div className="text-center">
                <div className="flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mx-auto mb-4">
                  <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-foreground text-base leading-relaxed">
                  Password aggiornata con successo.
                </p>
                <p className="text-muted-foreground text-sm mt-2">
                  Verrai reindirizzato al login tra pochi secondi...
                </p>
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  Scegli una nuova password
                </h1>
                <p className="text-muted-foreground text-sm mb-6">
                  Inserisci la nuova password per il tuo account Volaptic.
                </p>

                <form onSubmit={handleSubmit} noValidate>
                  <div className="mb-4">
                    <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1.5">
                      Nuova password
                    </label>
                    <input
                      id="password"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e?.target?.value)}
                      placeholder="Minimo 8 caratteri"
                      className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors duration-200"
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-1.5">
                      Conferma password
                    </label>
                    <input
                      id="confirmPassword"
                      type="password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e?.target?.value)}
                      placeholder="Ripeti la nuova password"
                      className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors duration-200"
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-red-600 mb-4">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !password || !confirmPassword}
                    className="w-full py-2.5 px-4 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {loading ? 'Salvataggio...' : 'Salva nuova password'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UpdatePassword;
